import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { type AuthUser } from "wasp/auth";
import {
  getRecycleBinEntries,
  getRecycleBinStats,
  restoreFromRecycleBin,
  extendDeletionDate,
  cleanupExpiredEntries,
  deleteRecycleBinEntry,
} from "wasp/client/operations";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Loader2,
  Trash2,
  Clock,
  AlertTriangle,
  BarChart3,
  User,
  Info,
  Search,
} from "lucide-react";
import DefaultLayout from "../layout/DefaultLayout";
import Breadcrumb from "../layout/Breadcrumb";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { AdminFlyoutPane } from "../components/AdminFlyoutPane";
import {
  FlyoutGridLayout,
  FlyoutGridSection,
  FlyoutGridRow,
} from "../components/FlyoutGridLayout";
import { TableFilterLayout } from "../../components/ui/table-filter-layout";

interface RecycleBinEntry {
  id: string;
  originalContentId: string;
  contentType: string;
  locationId: string;
  authorId: string;
  title: string;
  content: any;
  rejectionReason?: string | null;
  rejectionNotes?: string | null;
  rejectedBy: string;
  rejectedAt: Date;
  scheduledDeletionAt: Date;
  restoredAt?: Date | null;
  restoredBy?: string | null;
  showExtendForm?: boolean;
  author?: {
    id: string;
    email: string | null;
    username: string | null;
  };
  rejector?: {
    id: string;
    email: string | null;
    username: string | null;
  };
  location?: {
    id: string;
    name: string;
  };
}

interface RecycleBinStats {
  totalEntries: number;
  entriesByType: Record<string, number>;
  entriesByReason: Record<string, number>;
  expiredEntries: number;
}

const CONTENT_TYPES = ["FACILITY", "NOTICE", "TENANT", "EVENT"];
const REJECTION_REASONS = [
  "INACCURATE_INFO",
  "INAPPROPRIATE_CONTENT",
  "DUPLICATE",
  "POLICY_VIOLATION",
  "INCOMPLETE_INFO",
  "OTHER",
];

export default function RecycleBinPage({ user }: { user: AuthUser }) {
  const { selectedLocation: contextLocation } = useLocationContext();

  // Use location from context instead of local state
  const selectedLocation = contextLocation?.id || "";
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [reasonFilter, setReasonFilter] = useState<string>("all");
  const [selectedEntry, setSelectedEntry] = useState<RecycleBinEntry | null>(
    null,
  );
  const [newDeletionDate, setNewDeletionDate] = useState<string>("");
  const [isRestoring, setIsRestoring] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentEntryIndex, setCurrentEntryIndex] = useState<number>(-1);

  // Get recycle bin stats
  const {
    data: stats,
    isLoading: loadingStats,
    refetch: refetchStats,
  } = useQuery(
    getRecycleBinStats,
    {
      locationId: selectedLocation,
    },
    {
      enabled: !!selectedLocation,
    },
  );

  // Get all recycle bin entries (no type filter)
  const {
    data: allEntries,
    isLoading: loadingEntries,
    refetch: refetchEntries,
  } = useQuery(
    getRecycleBinEntries,
    {
      locationId: selectedLocation,
      contentType: undefined, // Get all types
    },
    {
      enabled: !!selectedLocation,
    },
  );

  const entries = allEntries || [];

  const restoreAction = useAction(restoreFromRecycleBin);
  const extendDeletionDateAction = useAction(extendDeletionDate);
  const cleanupAction = useAction(cleanupExpiredEntries);
  const deleteRecycleBinEntryAction = useAction(deleteRecycleBinEntry);

  const handleRestore = async (entryId: string) => {
    if (!confirm("Are you sure you want to restore this content?")) {
      return;
    }

    setIsRestoring(true);
    try {
      await restoreAction({ recycleBinId: entryId });
      setSelectedEntry(null);
      refetchEntries();
      refetchStats();
      alert("Content restored successfully");
    } catch (error) {
      console.error("Error restoring content:", error);
      alert("Failed to restore content");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleExtendDeletionDate = async (entryId: string) => {
    if (!newDeletionDate) {
      alert("Please select a new deletion date");
      return;
    }

    setIsExtending(true);
    try {
      await extendDeletionDateAction({
        recycleBinId: entryId,
        newDeletionDate: new Date(newDeletionDate),
      });
      setNewDeletionDate("");
      setSelectedEntry(null);
      refetchEntries();
      alert("Deletion date extended successfully");
    } catch (error) {
      console.error("Error extending deletion date:", error);
      alert("Failed to extend deletion date");
    } finally {
      setIsExtending(false);
    }
  };

  const handleCleanup = async () => {
    if (
      !confirm(
        "Are you sure you want to permanently delete all expired entries? This action cannot be undone.",
      )
    ) {
      return;
    }

    setIsCleaning(true);
    try {
      const result = await cleanupAction({});
      refetchEntries();
      refetchStats();
      alert(`Successfully deleted ${result.deletedCount} expired entries`);
    } catch (error) {
      console.error("Error cleaning up expired entries:", error);
      alert("Failed to clean up expired entries");
    } finally {
      setIsCleaning(false);
    }
  };

  const handlePermanentDelete = async (entryId: string) => {
    if (
      !confirm(
        "Are you sure you want to permanently delete this entry? This action cannot be undone and will remove it from the recycle bin.",
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteRecycleBinEntryAction({ recycleBinId: entryId });
      setSelectedEntry(null);
      refetchEntries();
      refetchStats();
      alert("Entry permanently deleted successfully");
    } catch (error) {
      console.error("Error permanently deleting entry:", error);
      alert("Failed to permanently delete entry");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateInput: string | Date) => {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDaysUntilDeletion = (deletionDate: string | Date) => {
    const now = new Date();
    const deletion =
      typeof deletionDate === "string" ? new Date(deletionDate) : deletionDate;
    const diffTime = deletion.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRejectionReasonLabel = (reason: string) => {
    return reason
      .replace("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const isExpired = (deletionDate: string | Date) => {
    const deletion =
      typeof deletionDate === "string" ? new Date(deletionDate) : deletionDate;
    return deletion < new Date();
  };

  const isExpiringSoon = (deletionDate: string | Date) => {
    const daysUntilDeletion = getDaysUntilDeletion(deletionDate);
    return daysUntilDeletion <= 3 && daysUntilDeletion > 0;
  };

  // Navigation handlers
  const handleNavigatePrevious = () => {
    if (currentEntryIndex > 0 && entries) {
      const newIndex = currentEntryIndex - 1;
      setCurrentEntryIndex(newIndex);
      setSelectedEntry(entries[newIndex]);
      setNewDeletionDate("");
    }
  };

  const handleNavigateNext = () => {
    if (entries && currentEntryIndex < entries.length - 1) {
      const newIndex = currentEntryIndex + 1;
      setCurrentEntryIndex(newIndex);
      setSelectedEntry(entries[newIndex]);
      setNewDeletionDate("");
    }
  };

  const hasPrevious = currentEntryIndex > 0;
  const hasNext = entries && currentEntryIndex < entries.length - 1;

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-20 bg-white dark:bg-sidebar-active transition-colors">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Recycle Bin
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage deleted content and restore items if needed
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter appears within the Entries tab to align with columns */}

        {/* Scrollable Content */}
        <div className="pt-[calc(var(--header-height)+2rem)]">
          <div className="pb-4">
            {/* Stats Badges */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 mx-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2">
                      <Trash2 className="h-8 w-8 text-gray-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {stats.totalEntries}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Total Entries
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-8 w-8 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {stats.expiredEntries}
                        </p>
                        <p className="text-sm text-muted-foreground">Expired</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="h-8 w-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {Object.keys(stats.entriesByType).length}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Content Types
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-8 w-8 text-yellow-500" />
                      <div>
                        <p className="text-2xl font-bold">
                          {stats.totalEntries - stats.expiredEntries}
                        </p>
                        <p className="text-sm text-muted-foreground">Active</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Main Content */}
            <Tabs defaultValue="entries" className="space-y-4">
              <div className="flex items-center justify-between mx-4">
                <TabsList>
                  <TabsTrigger value="entries">Recycle Bin Entries</TabsTrigger>
                  <TabsTrigger value="stats">Statistics</TabsTrigger>
                </TabsList>
                {stats && stats.expiredEntries > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleCleanup}
                    disabled={isCleaning}
                  >
                    {isCleaning ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cleaning...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Clean Expired ({stats.expiredEntries})
                      </>
                    )}
                  </Button>
                )}
              </div>

              <TabsContent value="entries" className="space-y-4">
                {!selectedLocation ? (
                  <Alert className="mx-4">
                    <AlertDescription>
                      Please select a location from the top bar to view recycle
                      bin.
                    </AlertDescription>
                  </Alert>
                ) : loadingEntries ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="w-full">
                    <TableFilterLayout
                      columns={[
                        { title: "Title", span: 3 },
                        { title: "Type", span: 1 },
                        { title: "Author", span: 2 },
                        { title: "Deleted", span: 2 },
                        { title: "Deletion Date", span: 2 },
                        { title: "Reason", span: 1 },
                      ]}
                      filters={[
                        {
                          span: 3,
                          content: (
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                              <input
                                type="text"
                                placeholder="Search title or author..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          ),
                        },
                        {
                          span: 1,
                          content: (
                            <select
                              value={typeFilter}
                              onChange={(e) => setTypeFilter(e.target.value)}
                              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="all">All types</option>
                              {CONTENT_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          ),
                        },
                        { span: 2, content: <div className="h-10" /> },
                        { span: 2, content: <div className="h-10" /> },
                        { span: 2, content: <div className="h-10" /> },
                        {
                          span: 1,
                          content: (
                            <select
                              value={reasonFilter}
                              onChange={(e) => setReasonFilter(e.target.value)}
                              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="all">All</option>
                              {REJECTION_REASONS.map((r) => (
                                <option key={r} value={r}>
                                  {r.replace("_", " ")}
                                </option>
                              ))}
                            </select>
                          ),
                        },
                      ]}
                    />

                    {/* Table Body */}
                    <div className="divide-y divide-gray-200">
                      {(() => {
                        const filteredEntries = entries
                          .filter((e: any) =>
                            typeFilter === "all"
                              ? true
                              : e.contentType === typeFilter,
                          )
                          .filter((e: any) =>
                            reasonFilter === "all"
                              ? true
                              : e.rejectionReason === reasonFilter,
                          )
                          .filter((e: any) => {
                            const s = searchTerm.toLowerCase();
                            if (!s) return true;
                            const title = e.title || "";
                            const author =
                              e.author?.username || e.author?.email || "";
                            return (
                              title.toLowerCase().includes(s) ||
                              author.toLowerCase().includes(s)
                            );
                          });

                        return filteredEntries.length > 0 ? (
                          filteredEntries.map(
                            (entry: RecycleBinEntry, index: number) => {
                              const daysUntilDeletion = getDaysUntilDeletion(
                                entry.scheduledDeletionAt,
                              );
                              const expired = isExpired(
                                entry.scheduledDeletionAt,
                              );
                              const expiringSoon = isExpiringSoon(
                                entry.scheduledDeletionAt,
                              );

                              return (
                                <div
                                  key={entry.id}
                                  className="admin-hoverable py-3 transition-colors cursor-pointer"
                                  onClick={() => {
                                    setCurrentEntryIndex(index);
                                    setSelectedEntry(entry);
                                  }}
                                  title="Click to view details"
                                >
                                  <div className="grid grid-cols-12 gap-4 items-start px-6">
                                    <div className="col-span-3 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {entry.title}
                                      </div>
                                      {entry.rejectionNotes && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                                          {entry.rejectionNotes}
                                        </div>
                                      )}
                                    </div>
                                    <div className="col-span-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                                        {entry.contentType}
                                      </div>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                        {entry.author?.username ||
                                          entry.author?.email ||
                                          "Unknown"}
                                      </div>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                        {formatDate(entry.rejectedAt)}
                                      </div>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                        {formatDate(entry.scheduledDeletionAt)}
                                      </div>
                                      {!expired && (
                                        <div
                                          className={`text-xs mt-1 ${expiringSoon
                                              ? "text-orange-600"
                                              : "text-gray-500"
                                            }`}
                                        >
                                          {daysUntilDeletion} days left
                                        </div>
                                      )}
                                      {expired && (
                                        <div className="text-xs mt-1 text-red-600 font-medium">
                                          Expired
                                        </div>
                                      )}
                                    </div>
                                    <div className="col-span-1">
                                      {entry.rejectionReason && (
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                          {getRejectionReasonLabel(
                                            entry.rejectionReason,
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )
                        ) : (
                          <div className="py-8 px-6">
                            <div className="text-center text-sm text-gray-500">
                              No deleted content found for the selected
                              location.
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="stats" className="space-y-4">
                {stats && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Entries by Content Type</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.entries(stats.entriesByType).map(
                            ([type, count]) => (
                              <div
                                key={type}
                                className="flex justify-between items-center"
                              >
                                <span className="text-sm">{type}</span>
                                <span className="text-sm font-medium text-muted-foreground">
                                  {count}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Entries by Rejection Reason</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.entries(stats.entriesByReason).map(
                            ([reason, count]) => (
                              <div
                                key={reason}
                                className="flex justify-between items-center"
                              >
                                <span className="text-sm">
                                  {getRejectionReasonLabel(reason)}
                                </span>
                                <span className="text-sm font-medium text-muted-foreground">
                                  {count}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Restore/Extend Flyout */}
            <AdminFlyoutPane
              open={!!selectedEntry}
              onOpenChange={(open) => {
                if (!open) {
                  setSelectedEntry(null);
                  setCurrentEntryIndex(-1);
                  setNewDeletionDate("");
                }
              }}
              title="Recycle Bin Entry"
              width="2xl"
              mode="view"
              onNavigatePrevious={handleNavigatePrevious}
              onNavigateNext={handleNavigateNext}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              headerActions={
                selectedEntry && (
                  <>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handlePermanentDelete(selectedEntry.id)}
                      disabled={isDeleting}
                      className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                    >
                      {isDeleting ? "Purging..." : "Purge"}
                    </Button>
                    {!isExpired(selectedEntry.scheduledDeletionAt) && (
                      <Button
                        onClick={() => handleRestore(selectedEntry.id)}
                        disabled={isRestoring}
                        size="sm"
                        className="w-[110px]"
                      >
                        {isRestoring ? "Restoring..." : "Restore"}
                      </Button>
                    )}
                  </>
                )
              }
            >
              {selectedEntry && (
                <>
                  <FlyoutGridLayout>
                    <FlyoutGridSection title="ENTRY DETAILS">
                      <FlyoutGridRow
                        label="Title"
                        value={selectedEntry.title}
                      />
                      <FlyoutGridRow
                        label="Content Type"
                        value={selectedEntry.contentType}
                      />
                      <FlyoutGridRow
                        label="Location"
                        value={
                          selectedEntry.location?.name || "Unknown Location"
                        }
                      />
                      <FlyoutGridRow
                        label="Author"
                        value={
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-500" />
                            <span>
                              {selectedEntry.author?.username ||
                                selectedEntry.author?.email ||
                                "Unknown"}
                            </span>
                          </div>
                        }
                      />
                      <FlyoutGridRow
                        label="Deleted On"
                        value={formatDate(selectedEntry.rejectedAt)}
                      />
                      <FlyoutGridRow
                        label="Scheduled Deletion"
                        value={
                          <div>
                            <div>
                              {formatDate(selectedEntry.scheduledDeletionAt)}
                            </div>
                            {!isExpired(selectedEntry.scheduledDeletionAt) && (
                              <div
                                className={`text-xs mt-1 ${isExpiringSoon(
                                  selectedEntry.scheduledDeletionAt,
                                )
                                    ? "text-orange-600"
                                    : "text-gray-500"
                                  }`}
                              >
                                {getDaysUntilDeletion(
                                  selectedEntry.scheduledDeletionAt,
                                )}{" "}
                                days remaining
                              </div>
                            )}
                            {isExpired(selectedEntry.scheduledDeletionAt) && (
                              <div className="text-xs mt-1 text-red-600 font-medium">
                                Expired - Pending permanent deletion
                              </div>
                            )}
                          </div>
                        }
                      />
                      <FlyoutGridRow
                        label="Rejection Reason"
                        value={
                          selectedEntry.rejectionReason
                            ? getRejectionReasonLabel(
                              selectedEntry.rejectionReason,
                            )
                            : "Not specified"
                        }
                      />
                      <FlyoutGridRow
                        label="Rejection Notes"
                        value={
                          selectedEntry.rejectionNotes || "No notes provided"
                        }
                      />
                      <FlyoutGridRow
                        label="Deleted By"
                        value={
                          selectedEntry.rejector?.username ||
                          selectedEntry.rejector?.email ||
                          "Unknown"
                        }
                        isLast
                      />
                    </FlyoutGridSection>
                  </FlyoutGridLayout>

                  {!isExpired(selectedEntry.scheduledDeletionAt) && (
                    <Alert className="mt-6">
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Restoring this content will make it visible again and
                        automatically approve it.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </AdminFlyoutPane>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
}
